import React, {useState} from "react";
import {useNavigate} from "react-router-dom";
import Modal from "./ui/Modal";
import Icon from "./ui/Icon";
import CustomRange from "./CustomRange";
import {HANDICAP_OPTIONS, OptionTile, STAGE_OPTIONS, TIME_PRESETS} from "./CreateGameModal";
import {LEVELS, levelInfo} from "../utils/engine";
import loadRandomPosition from "../clients/GameClient";

const SETTINGS_KEY = "chesscorner.computerSettings";

const DEFAULT_SETTINGS = {
    level: 3,
    color: "random",
    variant: "standard",
    stageId: 2,
    handicapId: 1,
    isCustomJumpstart: false,
    evalRange: [0, 5],
    moveRange: [10, 100],
    wantsAdvantage: false,
    useClock: false,
    presetId: 5,
    isCustomTime: false,
    customMinutes: 5,
    customIncrement: 3
};

// {baseMs, incrementMs} for the chosen clock, or null for no clock
export const timeControlFor = (settings) => {
    if (!settings.useClock) return null;
    const preset = TIME_PRESETS.find((p) => p.id === settings.presetId) || TIME_PRESETS[4];
    const minutes = settings.isCustomTime ? settings.customMinutes : preset.minutes;
    const increment = settings.isCustomTime ? settings.customIncrement : preset.increment;
    return {baseMs: Math.round(minutes * 60000), incrementMs: Math.round(increment * 1000)};
};

export const readComputerSettings = () => {
    try {
        const saved = JSON.parse(localStorage.getItem(SETTINGS_KEY) || "null");
        if (saved && saved.level && saved.color) return {...DEFAULT_SETTINGS, ...saved};
    } catch (e) {
        // ignore
    }
    return DEFAULT_SETTINGS;
};

const saveComputerSettings = (settings) => {
    try {
        localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings));
    } catch (e) {
        // ignore
    }
};

// The Jumpstart part of the settings, which is kept with the game so "Play again" can find a similar position
export const jumpstartFilters = (settings) => {
    const evals = settings.isCustomJumpstart ? settings.evalRange
        : (HANDICAP_OPTIONS.find((o) => o.id === settings.handicapId) || HANDICAP_OPTIONS[0]).range;
    const stage = settings.isCustomJumpstart ? settings.moveRange
        : (STAGE_OPTIONS.find((o) => o.id === settings.stageId) || STAGE_OPTIONS[1]).range;
    return {evals, stage, wantsAdvantage: !!settings.wantsAdvantage};
};

// Fetches a random Jumpstart position. You get the side that matches your wish to start with (or without) the advantage,
// the same way it works in online games.
export const pickJumpstartPosition = async ({evals, stage, wantsAdvantage}) => {
    const snapshot = await loadRandomPosition(evals[0], evals[1], stage[0], stage[1]);
    if (!snapshot || !snapshot.fen) throw new Error("No position was found. Try other settings, or try again in a moment.");
    const whiteIsBetter = snapshot.evaluation > 0;
    return {fen: snapshot.fen, color: wantsAdvantage === whiteIsBetter ? "white" : "black"};
};

// Choose a level and a colour (or a Jumpstart position), then start a game against the computer
const ComputerSetupModal = ({open, onClose}) => {
    const navigate = useNavigate();
    const [settings, setSettings] = useState(readComputerSettings);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const chosen = levelInfo(settings.level);
    const update = (changes) => setSettings((s) => ({...s, ...changes}));
    const isJumpstart = settings.variant === "jumpstart";

    if (!open) return null;

    const close = () => {
        setError(null);
        onClose();
    };

    const start = async () => {
        saveComputerSettings(settings);
        const request = {
            newGame: true,
            level: settings.level,
            color: settings.color,
            timeControl: timeControlFor(settings),
            requestedAt: Date.now()
        };
        if (isJumpstart) {
            setLoading(true);
            setError(null);
            try {
                const filters = jumpstartFilters(settings);
                const position = await pickJumpstartPosition(filters);
                request.initialFen = position.fen;
                request.color = position.color;
                request.jumpstart = filters;
            } catch (e) {
                setError(e.message || "Could not find a position. Try again in a moment.");
                return;
            } finally {
                setLoading(false);
            }
        }
        close();
        navigate("/computer", {state: request});
    };

    return (
        <Modal
            onClose={close}
            title="Play the computer"
            labelledBy="computer-setup-title"
            footer={<>
                <button className="btn" onClick={close}>Cancel</button>
                <button className="btn btn-primary" onClick={start} disabled={loading}>
                    {loading ? <span className="spinner spinner--sm"/> : <Icon name="cpu"/>}
                    {loading ? "Finding a position…" : "Start game"}
                </button>
            </>}
        >
            <div className="modal-body">
                <div className="option-group">
                    <div className="option-group__label">Variant</div>
                    <div className="segmented" style={{width: "100%"}}>
                        {[["standard", "Standard"], ["jumpstart", "Jumpstart"]].map(([value, label]) => (
                            <button key={value} type="button" className={settings.variant === value ? "active" : ""}
                                    style={{flex: 1, justifyContent: "center"}}
                                    onClick={() => update({variant: value})}>{label}</button>
                        ))}
                    </div>
                    {isJumpstart && (
                        <p className="small muted" style={{marginTop: "0.4rem"}}>
                            Jumpstart drops you into a random position from a real game, where one side starts with an advantage.
                        </p>
                    )}
                </div>

                <div className="option-group">
                    <div className="option-group__label">
                        <span>Level</span>
                        <span className="small muted">{chosen.name}: {chosen.blurb}</span>
                    </div>
                    <div className="option-grid level-grid">
                        {LEVELS.map((level) => (
                            <button key={level.level} type="button"
                                    className={`option-tile ${settings.level === level.level ? "selected" : ""}`}
                                    aria-pressed={settings.level === level.level}
                                    onClick={() => update({level: level.level})}>
                                <span className="option-tile__value">{level.level}</span>
                                <span className="option-tile__title">{level.name}</span>
                            </button>
                        ))}
                    </div>
                </div>

                <div className="option-group">
                    <div className="option-group__label">
                        <span>Clock</span>
                        {settings.useClock && (
                            <button type="button" className="link-btn" onClick={() => update({isCustomTime: !settings.isCustomTime})}>
                                {settings.isCustomTime ? "Show presets" : "Custom time"}
                            </button>
                        )}
                    </div>
                    <div className="segmented" style={{width: "100%"}}>
                        {[[false, "No clock"], [true, "Play on the clock"]].map(([value, label]) => (
                            <button key={label} type="button" className={settings.useClock === value ? "active" : ""}
                                    style={{flex: 1, justifyContent: "center"}}
                                    onClick={() => update({useClock: value})}>{label}</button>
                        ))}
                    </div>
                    {settings.useClock && (settings.isCustomTime ? (
                        <div className="stack stack--sm" style={{marginTop: "0.75rem"}}>
                            <div className="field">
                                <label htmlFor="cpu-minutes-slider">Minutes per player: <strong>{settings.customMinutes}</strong></label>
                                <input id="cpu-minutes-slider" className="slider" type="range" min="0.5" max="60" step="0.5"
                                       value={settings.customMinutes} onChange={(e) => update({customMinutes: Number(e.target.value)})}/>
                            </div>
                            <div className="field">
                                <label htmlFor="cpu-increment-slider">Increment in seconds: <strong>{settings.customIncrement}</strong></label>
                                <input id="cpu-increment-slider" className="slider" type="range" min="0" max="30" step="1"
                                       value={settings.customIncrement} onChange={(e) => update({customIncrement: Number(e.target.value)})}/>
                            </div>
                        </div>
                    ) : (
                        <div className="option-grid" style={{marginTop: "0.75rem"}}>
                            {TIME_PRESETS.map((option) => (
                                <OptionTile key={option.id} selected={settings.presetId === option.id}
                                            onClick={() => update({presetId: option.id})}
                                            imageSrc={option.variant.imageSrc}
                                            title={option.variant.title}
                                            value={`${option.minutes} + ${option.increment}`}/>
                            ))}
                        </div>
                    ))}
                    {settings.useClock && (
                        <p className="small muted" style={{marginTop: "0.4rem"}}>
                            The clock starts after the first move. Takebacks are off in games with a clock.
                        </p>
                    )}
                </div>

                {!isJumpstart ? (
                    <div className="option-group">
                        <div className="option-group__label">Play as</div>
                        <div className="color-choice">
                            {[["white", "White"], ["random", "Random"], ["black", "Black"]].map(([value, label]) => (
                                <button key={value} type="button" className={settings.color === value ? "selected" : ""}
                                        onClick={() => update({color: value})} aria-pressed={settings.color === value}>
                                    <span className={`color-pip color-pip--${value}`}/>
                                    {label}
                                </button>
                            ))}
                        </div>
                    </div>
                ) : (
                    <>
                        <div className="option-group">
                            <div className="option-group__label">
                                <span>{settings.isCustomJumpstart ? "Custom position" : "Game stage"}</span>
                                <button type="button" className="link-btn"
                                        onClick={() => update({isCustomJumpstart: !settings.isCustomJumpstart})}>
                                    {settings.isCustomJumpstart ? "Show presets" : "Fine tune"}
                                </button>
                            </div>
                            {settings.isCustomJumpstart ? (
                                <div className="stack">
                                    <CustomRange title="Advantage (pawns)" min={0} max={5} step={0.2}
                                                 initial={settings.evalRange} onChange={(evalRange) => update({evalRange})}
                                                 format={(v) => Number(v).toFixed(1)}/>
                                    <CustomRange title="Moves played" min={10} max={100} step={1}
                                                 initial={settings.moveRange} onChange={(moveRange) => update({moveRange})}/>
                                </div>
                            ) : (
                                <div className="option-grid" style={{gridTemplateColumns: "repeat(3, 1fr)"}}>
                                    {STAGE_OPTIONS.map((option) => (
                                        <OptionTile key={option.id} selected={settings.stageId === option.id}
                                                    onClick={() => update({stageId: option.id})} imageSrc={option.imageSrc}
                                                    title={`moves ${option.range[0]}-${option.range[1]}`}
                                                    value={option.title}/>
                                    ))}
                                </div>
                            )}
                        </div>
                        {!settings.isCustomJumpstart && (
                            <div className="option-group">
                                <div className="option-group__label">Handicap</div>
                                <div className="option-grid" style={{gridTemplateColumns: "repeat(3, 1fr)"}}>
                                    {HANDICAP_OPTIONS.map((option) => (
                                        <OptionTile key={option.id} selected={settings.handicapId === option.id}
                                                    onClick={() => update({handicapId: option.id})} imageSrc={option.imageSrc}
                                                    title={`${option.range[0]}-${option.range[1]} pawns`}
                                                    value={option.title}/>
                                    ))}
                                </div>
                            </div>
                        )}
                        <div className="option-group">
                            <label className="check-row">
                                <input type="checkbox" checked={settings.wantsAdvantage}
                                       onChange={() => update({wantsAdvantage: !settings.wantsAdvantage})}/>
                                I want to start with the advantage
                            </label>
                        </div>
                    </>
                )}

                <p className="small muted" style={{margin: "1rem 0 0"}}>
                    Games against the computer never change your rating. When you are logged in, they are saved to your profile.
                </p>

                {error && <div className="form-error" style={{marginTop: "1rem"}}>{error}</div>}
            </div>
        </Modal>
    );
};

export default ComputerSetupModal;
